class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  /**
   * Refresh the drawer from the CART DRAWER section.
   *
   * CartItems.onCartUpdate() (cart.js) is hardcoded to fetch
   * `?section_id=main-cart-items` — the cart *page* section — and assign it
   * straight into this element. That's right for <cart-items> on the cart
   * page, but this class inherits it, so any PUB_SUB_EVENTS.cartUpdate event
   * made the drawer replace its own contents with cart-page markup: the
   * "Shopping Cart" heading, "Clear Cart", the product table and "GO TO CART",
   * layered over the drawer's own empty state.
   *
   * That event fires from several places — cart.js itself, the stock
   * product-form.js, and productoption.js — so this affected the main site's
   * drawer as much as off-road's. Newer Dawn fixes it upstream by branching on
   * the element type; this is that fix, kept inside the drawer class so
   * cart.js and the cart page stay untouched.
   */
  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        /* Replace the whole .drawer__inner, not just this element.
         *
         * <cart-drawer-items> contains ONLY the line items. The drawer's
         * heading and its item count, the empty-cart panel, the subtotal and
         * the checkout button are all siblings of it inside .drawer__inner.
         * Swapping just this element's innerHTML refreshed the items and left
         * everything around them stale — the drawer showed the new products
         * under a "Your Cart is Empty" panel, with the heading still reading
         * "0 items" and a $0.00 subtotal.
         *
         * Taking .drawer__inner wholesale is what Dawn's own renderContents
         * does for this section, so the drawer ends up in exactly the state the
         * server rendered. Note this detaches `this`; the references below are
         * re-queried from the root for that reason. */
        const root = this.closest('cart-drawer');
        const inner = root && root.querySelector('.drawer__inner');
        const freshInner = html.querySelector('.drawer__inner');
        if (!inner || !freshInner) return;

        inner.innerHTML = freshInner.innerHTML;

        /* Carry the empty/not-empty state across too, not just the markup.
         *
         * `is-empty` is set by snippets/cart-drawer.liquid on BOTH the
         * <cart-drawer> root and <cart-drawer-items>, and
         * component-cart-drawer.css keys its empty state off it. Replacing
         * innerHTML alone left those flags exactly as the page first rendered
         * them — so adding the first item to an empty cart drew the "Your Cart
         * is Empty" panel AND the new line item at the same time.
         *
         * This path matters because the bcpo add-to-cart in productoption.js
         * never calls renderContents; it publishes cartUpdate and opens the
         * popup, so this subscriber is the only thing that refreshes the drawer.
         *
         * Read from the freshly fetched section rather than recomputed here, so
         * the server's own rendering stays the single source of truth. */
        /* `is-empty` also sits on the <cart-drawer> root, which is OUTSIDE the
         * markup just replaced, so it has to be set explicitly. The copy on
         * <cart-drawer-items> came in with the new HTML and is already correct.
         * `this` is detached by the innerHTML swap above — everything here is
         * re-queried from the root. */
        const freshRoot = html.querySelector('cart-drawer');
        const empty = freshRoot ? freshRoot.classList.contains('is-empty') : false;

        root.classList.toggle('is-empty', empty);
        inner.classList.toggle('is-empty', empty);
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
